import { useSearchParams } from 'react-router';
import { DataTableSortStatus } from 'mantine-datatable';
import { TABLE_CONSTANTS } from '../constants/table';
import { useState, useEffect } from 'react';

export function useInventoryUrlState() {
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get('page') ?? '1');
  const query = searchParams.get('query') ?? '';
  const nameQuery = searchParams.get('nameQuery') ?? '';
  const selectedCategories = searchParams.get('categories')?.split(',') ?? [];
  const selectedManufacturers =
    searchParams.get('manufacturers')?.split(',') ?? [];
  const selectedCurrencies = searchParams.get('currencies')?.split(',') ?? [];

  const selectedCurrencyTypes = {
    selling: searchParams.get('selling_currency')?.split(',') ?? [],
    purchase: searchParams.get('purchase_currency')?.split(',') ?? [],
    estimated: searchParams.get('estimated_currency')?.split(',') ?? [],
  };

  const globalCurrency = searchParams.get('global_currency') ?? undefined;
  const setQuery = (newQuery: string) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);

        if (!newQuery) {
          newParams.delete('query');
          newParams.delete('page');
        } else {
          newParams.set('query', newQuery);
          newParams.delete('page');
        }
        return newParams;
      },
      { replace: true }
    );
  };

  const sortStatus: DataTableSortStatus = {
    columnAccessor:
      searchParams.get('sortBy') ?? TABLE_CONSTANTS.DEFAULT_SORT.columnAccessor,
    direction:
      (searchParams.get('direction') as 'asc' | 'desc') ??
      TABLE_CONSTANTS.DEFAULT_SORT.direction,
  };

  const priceRange = (() => {
    const priceField =
      sortStatus.columnAccessor === 'selling_price'
        ? 'selling_price'
        : sortStatus.columnAccessor === 'purchase_price'
          ? 'purchase_price'
          : sortStatus.columnAccessor === 'estimated_selling_price'
            ? 'estimated_selling_price'
            : 'selling_price'; // default to selling_price if not specified

    const min = searchParams.get(`${priceField}Min`);
    const max = searchParams.get(`${priceField}Max`);
    if (!min && !max) return null;
    return [min ? parseInt(min) : null, max ? parseInt(max) : null] as [
      number | null,
      number | null,
    ];
  })();

  const setPage = (newPage: number) => {
    setSearchParams(
      (prev) => {
        if (newPage === 1) {
          prev.delete('page');
        } else {
          prev.set('page', newPage.toString());
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setNameQuery = (newQuery: string) => {
    setSearchParams(
      (prev) => {
        if (!newQuery) {
          prev.delete('nameQuery');
        } else {
          prev.set('nameQuery', newQuery);
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setSelectedCategories = (categories: string[]) => {
    setSearchParams(
      (prev) => {
        if (categories.length === 0) {
          prev.delete('categories');
        } else {
          prev.set('categories', categories.join(','));
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setSelectedManufacturers = (manufacturers: string[]) => {
    setSearchParams(
      (prev) => {
        if (manufacturers.length === 0) {
          prev.delete('manufacturers');
        } else {
          prev.set('manufacturers', manufacturers.join(','));
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setSelectedCurrencies = (currencies: string[]) => {
    setSearchParams(
      (prev) => {
        if (currencies.length === 0) {
          prev.delete('currencies');
        } else {
          prev.set('currencies', currencies.join(','));
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setSelectedCurrencyType = (
    type: 'selling' | 'purchase' | 'estimated',
    currencies: string[]
  ) => {
    setSearchParams(
      (prev) => {
        const param = `${type}_currency`;
        if (currencies.length === 0) {
          prev.delete(param);
        } else {
          prev.set(param, currencies.join(','));
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setGlobalCurrency = (currency: string | undefined) => {
    setSearchParams(
      (prev) => {
        if (!currency) {
          prev.delete('global_currency');
        } else {
          prev.set('global_currency', currency);
        }
        return prev;
      },
      { replace: true }
    );
  };

  const setPriceRange = (range: [number | null, number | null] | null) => {
    const priceField =
      sortStatus.columnAccessor === 'selling_price'
        ? 'selling_price'
        : sortStatus.columnAccessor === 'purchase_price'
          ? 'purchase_price'
          : sortStatus.columnAccessor === 'estimated_selling_price'
            ? 'estimated_selling_price'
            : 'selling_price'; // default to selling_price if not specified

    if (range === null) {
      setSearchParams(
        (prev) => {
          prev.delete(`${priceField}Min`);
          prev.delete(`${priceField}Max`);
          return prev;
        },
        { replace: true }
      );
    } else {
      const [min, max] = range;
      setSearchParams(
        (prev) => {
          if (min === null) {
            prev.delete(`${priceField}Min`);
          } else {
            prev.set(`${priceField}Min`, min.toString());
          }
          if (max === null) {
            prev.delete(`${priceField}Max`);
          } else {
            prev.set(`${priceField}Max`, max.toString());
          }
          return prev;
        },
        { replace: true }
      );
    }
  };

  const setSortStatus = (status: DataTableSortStatus) => {
    setSearchParams(
      (prev) => {
        prev.set('sortBy', status.columnAccessor);
        prev.set('direction', status.direction);
        return prev;
      },
      { replace: true }
    );
  };

  const resetFilters = () => {
    console.log(
      'useInventoryUrlState: before reset, params:',
      Object.fromEntries(searchParams.entries())
    );

    setSearchParams(
      (prev) => {
        prev.delete('query');
        prev.delete('nameQuery');
        prev.delete('categories');
        prev.delete('manufacturers');
        prev.delete('currencies');
        prev.delete('selling_currency');
        prev.delete('purchase_currency');
        prev.delete('estimated_currency');
        prev.delete('global_currency');
        prev.delete('selling_priceMin');
        prev.delete('selling_priceMax');
        prev.delete('purchase_priceMin');
        prev.delete('purchase_priceMax');
        prev.delete('estimated_selling_priceMin');
        prev.delete('estimated_selling_priceMax');
        prev.delete('page');
        return prev;
      },
      { replace: true }
    );

    console.log(
      'useInventoryUrlState: after reset, params:',
      Object.fromEntries(searchParams.entries())
    );
  };

  return {
    page,
    query,
    nameQuery,
    selectedCategories,
    selectedManufacturers,
    selectedCurrencies,
    selectedCurrencyTypes,
    globalCurrency,
    priceRange,
    sortStatus,
    setPage,
    setQuery,
    setNameQuery,
    setSelectedCategories,
    setSelectedManufacturers,
    setSelectedCurrencies,
    setSelectedCurrencyType,
    setGlobalCurrency,
    setPriceRange,
    setSortStatus,
    resetFilters,
  };
}
